import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Benchmark Generation nodes - showing evolution of performance benchmarks
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisBenchmarkPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Create performance benchmarks for my data processing pipeline"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Code Analysis
    {
      id: 'code-analysis',
      data: { label: '📊 Pipeline Analysis\n• Data processor: 1200 LOC\n• No existing benchmarks\n• Handles CSV, JSON, XML\n• Performance critical' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Benchmark
    {
      id: 'initial-benchmark',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline.bench.js',
        code: '// Basic benchmark\n' +
              'const start = Date.now();\n' +
              'processData(testData);\n' +
              'const end = Date.now();\n' +
              'console.log(`Time: ${end - start}ms`);',
        issues: 'No statistical analysis, single data size, no profiling',
        score: 1.5
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '🎯 Benchmark Quality Metrics' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'statistical-score',
      data: { label: '📊 Statistical Rigor\nScore: 1/5\nNo std deviation' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'scenario-score',
      data: { label: '🎭 Test Scenarios\nScore: 1.5/5\nOnly 1 data size' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'profiling-score',
      data: { label: '🔍 Profiling Depth\nScore: 0/5\nNo CPU/Memory data' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'exec-feedback',
      data: { label: '⚡ Execution Data\n⏱️ Avg: 245ms\n📈 High variance' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'human-feedback',
      data: { label: '👤 Developer Input\n"Need memory usage"\nScore: 2/5' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 Genetic Algorithm Engine\n\n🔄 Mutation: Vary workload patterns\n🔗 Crossover: Combine measurement strategies\n📈 Selection: Favor comprehensive benchmarks\n🎲 Population: 15 benchmark variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 Specialized Benchmark Agents' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'perf-analyst',
      data: { label: '⚡ Performance Analyst\n\n📝 Prompt:\n"Profile code and create\nbenchmarks for critical paths.\nMeasure:\n- Execution time\n- Memory allocation\n- CPU utilization"' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'load-tester',
      data: { label: '📊 Load Test Designer\n\n📝 Prompt:\n"Generate realistic workload\nscenarios including:\n- Small/Medium/Large data\n- Concurrent operations\n- Edge case inputs"' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'stats-expert',
      data: { label: '📈 Statistical Expert\n\n📝 Prompt:\n"Ensure statistical validity:\n- Multiple iterations\n- Warmup runs\n- Standard deviation\n- Percentile analysis"' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Enhanced Benchmarks (Population: 15)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 350 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline-perf.bench.js (Variant A)',
        code: '// Performance profiling benchmark\n' +
              '+ import { performance } from "perf_hooks";\n' +
              '+ import { cpuUsage, memoryUsage } from "process";\n' +
              '+ \n' +
              '+ async function benchmarkPipeline() {\n' +
              '+   const startCpu = cpuUsage();\n' +
              '+   const startMem = memoryUsage();\n' +
              '+   const startTime = performance.now();\n' +
              '+   \n' +
              '+   await processData(testData);\n' +
              '+   \n' +
              '+   const endTime = performance.now();\n' +
              '+   const endCpu = cpuUsage(startCpu);\n' +
              '+   const endMem = memoryUsage();\n' +
              '+   \n' +
              '+   return {\n' +
              '+     time: endTime - startTime,\n' +
              '+     cpu: endCpu.user + endCpu.system,\n' +
              '+     memory: endMem.heapUsed - startMem.heapUsed\n' +
              '+   };\n' +
              '+ }',
        score: 3.2
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline-load.bench.js (Variant B)',
        code: '// Load testing benchmark\n' +
              '+ suite("DataPipeline Load Tests", () => {\n' +
              '+   const dataSizes = [\n' +
              '+     { name: "1MB", data: generateData(1) },\n' +
              '+     { name: "10MB", data: generateData(10) },\n' +
              '+     { name: "100MB", data: generateData(100) },\n' +
              '+     { name: "1GB", data: generateData(1000) }\n' +
              '+   ];\n' +
              '+   \n' +
              '+   dataSizes.forEach(({ name, data }) => {\n' +
              '+     benchmark(`Process ${name}`, () => {\n' +
              '+       processData(data);\n' +
              '+     }, {\n' +
              '+       iterations: 100,\n' +
              '+       warmup: 10\n' +
              '+     });\n' +
              '+   });\n' +
              '+ });',
        score: 3.5
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline-stats.bench.js (Variant C)',
        code: '// Statistical benchmark\n' +
              '+ import { Benchmark } from "benchmark";\n' +
              '+ \n' +
              '+ const suite = new Benchmark.Suite();\n' +
              '+ \n' +
              '+ suite.add("Pipeline#process", () => {\n' +
              '+   processData(standardData);\n' +
              '+ })\n' +
              '+ .on("cycle", (event) => {\n' +
              '+   const stats = event.target.stats;\n' +
              '+   console.log(`Mean: ${stats.mean}`);\n' +
              '+   console.log(`Std Dev: ${stats.deviation}`);\n' +
              '+   console.log(`95th %ile: ${stats.p95}`);\n' +
              '+ })\n' +
              '+ .run({ async: true });',
        score: 3.8
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 12 more variants\nwith different strategies' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Crossover & Mutation',
        scores: {
          'Top Score': '3.8/5',
          'Statistical': '3.5/5',
          'Scenarios': '3.2/5',
          'Profiling': '3.0/5'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Combined Benchmarks' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 300 },
    },
    
    // Generation 2 - Combined Features
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline-comprehensive.bench.js (Elite)',
        code: '// Comprehensive benchmark suite\n' +
              '+ import { Suite } from "benchmarkify";\n' +
              '+ \n' +
              '+ const bench = new Suite("Data Pipeline", {\n' +
              '+   iterations: 1000,\n' +
              '+   warmup: 50,\n' +
              '+   profiler: true\n' +
              '+ });\n' +
              '+ \n' +
              '+ // Multiple data sizes (from Variant B)\n' +
              '+ // Statistical analysis (from Variant C)\n' +
              '+ // CPU/Memory profiling (from Variant A)\n' +
              '+ \n' +
              '+ ["1MB", "10MB", "100MB", "1GB"].forEach(size => {\n' +
              '+   bench.add(`Process ${size}`, async () => {\n' +
              '+     await processDataWithProfiling(data[size]);\n' +
              '+   });\n' +
              '+ });\n' +
              '+ \n' +
              '+ bench.run().then(results => {\n' +
              '+   generateReport(results);\n' +
              '+   detectRegressions(results);\n' +
              '+ });',
        score: 4.5
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline-concurrent.bench.js (Mutated)',
        code: '// Mutation: Concurrent load testing\n' +
              '+ async function benchmarkConcurrency() {\n' +
              '+   const concurrencyLevels = [1, 5, 10, 20, 50];\n' +
              '+   \n' +
              '+   for (const level of concurrencyLevels) {\n' +
              '+     const promises = Array(level).fill()\n' +
              '+       .map(() => processData(testData));\n' +
              '+     \n' +
              '+     const start = performance.now();\n' +
              '+     await Promise.all(promises);\n' +
              '+     const duration = performance.now() - start;\n' +
              '+     \n' +
              '+     console.log(`Concurrency ${level}: ${duration}ms`);\n' +
              '+     console.log(`Throughput: ${level * 1000 / duration} ops/sec`);\n' +
              '+   }\n' +
              '+ }',
        score: 4.2
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Production Ready',
        scores: {
          'Statistical': '4.8/5',
          'Scenarios': '4.9/5',
          'Profiling': '4.7/5',
          'Reporting': '4.8/5'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'pipeline.bench.js (Production)',
        code: '// Production-ready benchmark suite\n' +
              '// Features: Statistical analysis, profiling, regression detection\n' +
              '\n' +
              'import { BenchmarkSuite } from "./bench-framework";\n' +
              '\n' +
              '// Comprehensive benchmark with:\n' +
              '// ✅ Multiple data sizes (1MB to 1GB)\n' +
              '// ✅ Concurrency testing (1-50 parallel)\n' +
              '// ✅ Memory & CPU profiling\n' +
              '// ✅ Statistical significance testing\n' +
              '// ✅ Automated regression detection\n' +
              '// ✅ HTML/JSON report generation\n' +
              '\n' +
              '// Results:\n' +
              '// - 99th percentile: 145ms\n' +
              '// - Memory efficient: O(n) growth\n' +
              '// - Linear scaling up to 20 concurrent',
        score: 4.8
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ Benchmark Evolution Results\n\n• Found 2 performance bottlenecks\n• Identified memory leak at scale\n• Optimized concurrent processing\n• 35% performance improvement\n• Automated regression testing' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const benchmarkEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'code-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'code-analysis', target: 'initial-benchmark', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-benchmark', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'statistical-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'scenario-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'profiling-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'exec-feedback', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'human-feedback', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-benchmark', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'perf-analyst', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'load-tester', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'stats-expert', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'perf-analyst', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'load-tester', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'stats-expert', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'statistical-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'exec-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'human-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisBenchmarkGeneration = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(benchmarkEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisBenchmarkPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisBenchmarkPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          📊 Artemis Benchmark Generation - Evolving Performance Tests
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          See how AI evolves simple timing measurements into comprehensive performance benchmarks
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#627D98',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#486581'}
          onMouseLeave={(e) => e.target.style.background = '#627D98'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.3,
              maxZoom: 1.0
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #D9E2EC'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#627D98';
              }}
              nodeStrokeColor={() => '#486581'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#D9E2EC" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Insights */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #D9E2EC',
          color: '#102A43',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '320px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#5E60CE' }}>
            🧬 AI-Driven Benchmark Evolution
          </div>
          <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#486581' }}>
            Starting from basic timing, Artemis uses <span style={{ color: '#F4511E', fontWeight: '600' }}>genetic algorithms</span> to 
            evolve benchmarks that measure <span style={{ color: '#5E60CE', fontWeight: '600' }}>multiple performance dimensions</span> with 
            statistical rigor and automated regression detection.
          </div>
          <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(94, 96, 206, 0.1)', borderRadius: '8px' }}>
            <strong>Benchmark Quality:</strong> 1.5 → 4.8 
            <span style={{ color: '#28A745', fontWeight: '600' }}> (+220%)</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisBenchmarkGeneration; 